import React, { useState, memo, useMemo } from "react";
import {
  IconButton, Paper, styled, Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow, Box, TextField
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import "../InventoryDetail.css";
import { InventoryViewModal } from "../../../../Components/DetailsPageLowerLayer/InventoryViewModal";
import { useTheme } from "@mui/material/styles";
import { useHighlightMarkIdx } from "./InventoryGraphView";
import { formatValue } from "../../../../utils/currencyUtils";

const StyledTableCell = styled(TableCell)(({ theme, bgcolor, color, sticky, header }) => ({
  width: 95,
  letterSpacing: 'normal',
  padding: '12px 16px',
  fontSize: 13,
  borderBottom: 'none',
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: bgcolor ? bgcolor : theme.palette.common.black,
    color: color ? color : theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    paddingLeft: 8,
    paddingRight: 5
  },
  ...(sticky && {
    position: "sticky",
    left: 0,
    zIndex: header ? 5 : 3,
    backgroundColor: theme.palette.common.black,   // keep black header
    color: theme.palette.common.white
  })
}));

const StyledTableRow = styled(TableRow)(({ theme, bgcolor, color, fontWeight }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: bgcolor ? bgcolor : theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  backgroundColor: bgcolor,
  color,
  fontWeight
}));

const EDITABLE_LABELS = ["Receipts", "Requirements"];

export const RenderTable = memo(({ columns, rows, editedRows, handleValueChange, currentView, fullData }) => {
  const theme = useTheme();
  const { highlightMarkIdx } = useHighlightMarkIdx();

  return (
    <Paper sx={{ width: '100%' }}>
      <Table stickyHeader aria-label="customized table"
        sx={{
          borderCollapse: "separate",
          tableLayout: "fixed",
          minWidth: (columns.length * 136)
        }}>
        <TableHead>
          <TableRow>
            <StyledTableCell sticky header
              bgcolor={theme.palette.common.black}>Measures</StyledTableCell>
            {columns.map((d, i) => (
              <StyledTableCell
                key={`date-${i}`}
                bgcolor={highlightMarkIdx === i ? "#fff" : ''}
                color={highlightMarkIdx === i ? "#000" : ''}
                sx={{
                  paddingLeft: 1,
                  paddingRight: 2,
                  textAlign: 'center',
                  backgroundImage: 'var(--Paper-overlay)',
                  boxShadow: highlightMarkIdx === i
                    ? '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12'
                    : '',
                  transition: 'background 0.3s ease',
                }}
              >
                <div>{d.split('\n')[0]}</div>
                <div>{d.split('\n')[1]}</div>
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(rows).map((rowKey, i) => {
            const mainRowData = { id: rowKey, ...editedRows[rowKey] };
            return (
              <RowCell
                row={mainRowData}
                columns={columns}
                fullData={fullData} // full API dataset for real-time lookups
                currentView={currentView}
                onValueChange={handleValueChange}
              />
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
});

const RowCell = memo(({ row, columns, fullData, currentView, onValueChange }) => {
  const theme = useTheme();
  const validExpandableRows = ["Total Demand", "Total Supply"];
  const isExpandableRow = (key) => validExpandableRows.includes(key);
  const isEditableRow = currentView.simulate && EDITABLE_LABELS.includes(row.name);
  const [expandRow, setExpandRow] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [title, setTitle] = useState(null);
  const [rowValue, setRowValue] = useState([]);

  const handleCellClick = (rowData, cellValue, columnName) => {
    if (!isEditableRow) {
      setTitle(rowData.name);
      setRowValue([{ time: columnName, quantity: cellValue }]);
      setOpenModal(true);
    }
  };

  const renderRow = (label, quantities, isEditable, pastDue) => (
    <StyledTableRow key={`row-${label}`}>
      <StyledTableCell sticky
        component="th"
        scope="row"
        sx={{
          fontWeight: isExpandableRow(label) ? 'bold' : 'inherit',
          color: expandRow && isExpandableRow(label) ? theme.palette.primary.main : '',
        }}
      >
        {label}
        {isExpandableRow(label) && (
          <IconButton size="small" onClick={() => setExpandRow(!expandRow)}>
            {expandRow ? <KeyboardArrowUpIcon sx={{ color: '#fff' }} /> : <KeyboardArrowDownIcon sx={{ color: '#fff' }} />}
          </IconButton>
        )}
        {pastDue && <><br /><span style={{ fontSize: "11px" }}>Past Due: {formatValue(pastDue)}</span></>}
      </StyledTableCell>
      {quantities.map((d, idx) => (
        <EditRowCell
          key={`cell-${idx}`}
          isEditable={isEditable}
          idx={idx}
          rowId={row.id}
          rowName={label}
          d={d}
          columns={columns}
          onValueChange={onValueChange}
          handleCellClick={handleCellClick}
        />
      ))}
    </StyledTableRow>
  );

  const rowsToRender = [renderRow(row.name, row.quantities, isEditableRow, row.pastDue)];

  // Insert KO Projected Inventory Real Time after KO Projected Inventory By GR Date
  if (currentView.realTime && row.name === "KO Projected By GR Date") {
    const rtData = fullData?.koProjectedInventoryRealTime;
    if (rtData) {
      rowsToRender.push(renderRow(rtData.name, rtData.quantities || [], false, rtData.pastDue));
    }
  }

  if (expandRow && isExpandableRow(row.name)) {
    rowsToRender.push(
      <ExpandRowTable
        key="expand"
        row={row}
        fullData={fullData} // pass full dataset for nested Requirements/Receipts
        currentView={currentView}
        onValueChange={onValueChange}
      />
    );
  }

  return (
    <>
      {rowsToRender}
      <InventoryViewModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        rows={rowValue}
        columns={[
          { field: "time", headerName: "Time", flex: 1 },
          { field: "quantity", headerName: "Quantity", flex: 1 }
        ]}
        title={title}
      />
    </>
  );
});

const EditRowCell = memo(({ isEditable, idx, subIdx, rowId, rowName, d, onValueChange, columns }) => {
  const [editCell, setEditCell] = useState(null);
  const { highlightMarkIdx } = useHighlightMarkIdx();
  const cellKey = `${rowId || rowName}-${idx}`;
  const enabled = editCell === cellKey;
  const [cellValue, setCellValue] = useState(d);

  useMemo(() => {
    setCellValue(d);
  }, [d]);

  const onChange = (rowKey, idx, value) => {
    setCellValue(value);
    onValueChange(rowKey, idx, value, subIdx);
  };

  return (
    <StyledTableCell
      sx={{
        textAlign: 'center',
        backgroundColor: highlightMarkIdx === idx ? '#f2f3f4' : '',
        backgroundImage: 'var(--Paper-overlay)',
        boxShadow: highlightMarkIdx === idx
          ? '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12'
          : '',
        transition: 'background 0.3s ease',
      }}
    >
      {isEditable ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <TextField
            variant="standard"
            value={cellValue}
            onChange={(e) => onChange(rowId || rowName, idx, e.target.value)}
            disabled={!enabled}
            inputProps={{ style: { fontSize: 14, width: "70px" } }}
          />
          <EditIcon
            fontSize="small"
            sx={{
              ml: 0.5,
              color: enabled ? "#000000" : "#9aa0a6",
              cursor: "pointer"
            }}
            onClick={() => setEditCell(cellKey)}
          />
        </Box>
      ) : (
        formatValue(cellValue)
      )}
    </StyledTableCell>
  );
});

const ExpandRowTable = memo(({ row, fullData, currentView, onValueChange }) => {
  const theme = useTheme();
  const renderSubMeasureRow = (label, quantities, pastDue, isEditable) => (
    <StyledTableRow key={`subrow-${label}`} bgcolor={row.bgcolor}>
      <TableCell component="th" scope="row" width="fit-content"
        style={{
          fontSize: "12.5px",
          position: "sticky",
          left: 0,                  // distance from left
          zIndex: 3,                 // keep above others
          backgroundColor: theme.palette.common.black,   // keep black header
          color: theme.palette.common.white,
          borderBottom: 'none',
        }}>
        {label}
        {pastDue !== undefined && pastDue !== "" && (
          <>
            <br />
            <span style={{ fontSize: "11px" }}>Past Due: {formatValue(pastDue)}</span>
          </>
        )}
      </TableCell>
      {quantities.map((d, colIdx) => (
        <EditRowCell
          key={`subrow-${label}-${colIdx}`}
          isEditable={isEditable}
          idx={colIdx}
          subIdx={row.subMeasures.findIndex(sm => sm.name === label)}
          rowId={row.id}
          rowName={label}
          d={d}
          onValueChange={onValueChange}
        />
      ))}
    </StyledTableRow>
  );

  return row.subMeasures.map((subRow) => {
    const isEditableRow =
      currentView.simulate &&
      !currentView.realTime &&
      EDITABLE_LABELS.includes(subRow.name);

    const normalRow = renderSubMeasureRow(subRow.name, subRow.quantities, subRow.pastDue, isEditableRow);

    let realtimeRow = null;
    if (currentView.realTime) {
      let rtData = null;

      if (subRow.name === "Requirements") {
        rtData = fullData?.totalDemand?.subMeasures?.find(sm => sm.name === "Requirements-Real Time");
      } else if (subRow.name === "Receipts") {
        rtData = fullData?.totalSupply?.subMeasures?.find(sm => sm.name === "Receipts-Real Time");
      }

      if (rtData) {
        realtimeRow = renderSubMeasureRow(rtData.name, rtData.quantities || [], rtData.pastDue, false);
      }
    }

    return (
      <React.Fragment key={subRow.name}>
        {normalRow}
        {realtimeRow}
      </React.Fragment>
    );
  });
});